import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../Card';
import { Badge } from '../Badge';
import { Button } from '../Button';
import { Network, X, TrendingUp } from 'lucide-react';
type NodeType = 'PERSON' | 'CLUSTER' | 'NARRATIVE' | 'PLATFORM' | 'FOREIGN';
type GraphNode = {
  id: string;
  label: string;
  type: NodeType;
  x: number;
  y: number;
  influence: number;
  critical?: boolean;
  meta: string;
};
type GraphEdge = {
  from: string;
  to: string;
  strength: number;
};
const nodes: GraphNode[] = [{
  id: 'p1',
  label: '@budis_pengamat',
  type: 'PERSON',
  x: 180,
  y: 120,
  influence: 92,
  meta: 'Pengamat politik · 1.2M followers'
}, {
  id: 'p2',
  label: '@kritik_pedas99',
  type: 'PERSON',
  x: 250,
  y: 260,
  influence: 75,
  critical: true,
  meta: 'Akun anonim · 120K followers'
}, {
  id: 'p3',
  label: '@infodaerah_up',
  type: 'PERSON',
  x: 120,
  y: 320,
  influence: 68,
  meta: 'Buzzer terkoordinasi · 850K followers'
}, {
  id: 'c1',
  label: 'Cluster Bansos Hoax',
  type: 'CLUSTER',
  x: 380,
  y: 360,
  influence: 88,
  critical: true,
  meta: '247 akun aktif · Koordinasi tinggi'
}, {
  id: 'c2',
  label: 'Cluster #TolakKebijakanX',
  type: 'CLUSTER',
  x: 540,
  y: 130,
  influence: 81,
  meta: '1.247 akun · Aktivasi 02:00 WIB'
}, {
  id: 'n1',
  label: 'Narasi Anti-IKN',
  type: 'NARRATIVE',
  x: 660,
  y: 240,
  influence: 76,
  meta: 'Volume: 65K mention/24j'
}, {
  id: 'n2',
  label: 'Narasi Hoaks Pangan',
  type: 'NARRATIVE',
  x: 470,
  y: 280,
  influence: 84,
  critical: true,
  meta: 'Volume: 89K mention/24j'
}, {
  id: 'n3',
  label: 'Narasi Deepfake Presiden',
  type: 'NARRATIVE',
  x: 320,
  y: 60,
  influence: 95,
  critical: true,
  meta: 'Volume: 124K · KRITIS'
}, {
  id: 'pl1',
  label: 'TikTok',
  type: 'PLATFORM',
  x: 580,
  y: 380,
  influence: 70,
  meta: 'Platform amplifikasi utama'
}, {
  id: 'pl2',
  label: 'X / Twitter',
  type: 'PLATFORM',
  x: 700,
  y: 100,
  influence: 72,
  meta: 'Sumber narasi politik'
}, {
  id: 'pl3',
  label: 'Facebook Grup',
  type: 'PLATFORM',
  x: 60,
  y: 200,
  influence: 58,
  meta: 'Distribusi hoaks lokal'
}, {
  id: 'f1',
  label: 'Server Cluster Asing',
  type: 'FOREIGN',
  x: 730,
  y: 360,
  influence: 64,
  critical: true,
  meta: 'Geo: Asia Timur · IP cluster 23 node'
}];
const edges: GraphEdge[] = [{
  from: 'p1',
  to: 'n1',
  strength: 3
}, {
  from: 'p1',
  to: 'c2',
  strength: 2
}, {
  from: 'p2',
  to: 'n2',
  strength: 4
}, {
  from: 'p2',
  to: 'c1',
  strength: 3
}, {
  from: 'p3',
  to: 'c1',
  strength: 4
}, {
  from: 'p3',
  to: 'pl3',
  strength: 3
}, {
  from: 'c1',
  to: 'n2',
  strength: 4
}, {
  from: 'c1',
  to: 'pl1',
  strength: 3
}, {
  from: 'c2',
  to: 'n1',
  strength: 3
}, {
  from: 'c2',
  to: 'pl2',
  strength: 4
}, {
  from: 'n2',
  to: 'pl1',
  strength: 3
}, {
  from: 'n3',
  to: 'p1',
  strength: 2
}, {
  from: 'n3',
  to: 'pl2',
  strength: 4
}, {
  from: 'f1',
  to: 'c1',
  strength: 3
}, {
  from: 'f1',
  to: 'c2',
  strength: 2
}, {
  from: 'f1',
  to: 'n1',
  strength: 3
}];
const typeStyles: Record<NodeType, {
  fill: string;
  stroke: string;
  label: string;
}> = {
  PERSON: {
    fill: 'fill-primary/20',
    stroke: 'stroke-primary',
    label: 'Person'
  },
  CLUSTER: {
    fill: 'fill-red-500/20',
    stroke: 'stroke-red-500',
    label: 'Buzzer Cluster'
  },
  NARRATIVE: {
    fill: 'fill-amber-500/20',
    stroke: 'stroke-amber-500',
    label: 'Narrative'
  },
  PLATFORM: {
    fill: 'fill-slate-400/20',
    stroke: 'stroke-slate-500',
    label: 'Platform'
  },
  FOREIGN: {
    fill: 'fill-purple-500/20',
    stroke: 'stroke-purple-500',
    label: 'Foreign Actor'
  }
};
function NodeShape({
  node,
  hovered,
  selected,
  onEnter,
  onLeave,
  onClick
}: any) {
  const style = typeStyles[node.type as NodeType];
  const r = 22 + (node.influence - 60) * 0.3;
  const isActive = hovered || selected;
  const baseClass = `${style.fill} ${style.stroke} transition-all duration-200 cursor-pointer ${node.critical ? 'animate-pulse' : ''} ${isActive ? 'opacity-100' : 'opacity-90'}`;
  const common = {
    onMouseEnter: onEnter,
    onMouseLeave: onLeave,
    onClick: onClick,
    strokeWidth: isActive ? 3 : 2,
    className: baseClass
  };
  if (node.type === 'PERSON') {
    return <circle cx={node.x} cy={node.y} r={r} {...common} data-id="element-140" />;
  }
  if (node.type === 'CLUSTER') {
    const s = r * 1.6;
    return <rect x={node.x - s / 2} y={node.y - s / 2} width={s} height={s} rx={4} {...common} data-id="element-141" />;
  }
  if (node.type === 'NARRATIVE') {
    const s = r;
    const pts = `${node.x},${node.y - s} ${node.x + s},${node.y} ${node.x},${node.y + s} ${node.x - s},${node.y}`;
    return <polygon points={pts} {...common} data-id="element-142" />;
  }
  if (node.type === 'PLATFORM') {
    const s = r;
    const pts = [[node.x - s, node.y], [node.x - s / 2, node.y - s * 0.87], [node.x + s / 2, node.y - s * 0.87], [node.x + s, node.y], [node.x + s / 2, node.y + s * 0.87], [node.x - s / 2, node.y + s * 0.87]].map(p => p.join(',')).join(' ');
    return <polygon points={pts} {...common} data-id="element-143" />;
  }
  // FOREIGN — octagon
  const s = r;
  const o = s * 0.4;
  const pts = [[node.x - s, node.y - o], [node.x - o, node.y - s], [node.x + o, node.y - s], [node.x + s, node.y - o], [node.x + s, node.y + o], [node.x + o, node.y + s], [node.x - o, node.y + s], [node.x - s, node.y + o]].map(p => p.join(',')).join(' ');
  return <polygon points={pts} {...common} data-id="element-144" />;
}
export function ActorRelationshipGraph() {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const findNode = (id: string) => nodes.find(n => n.id === id)!;
  const connections = selectedNode ? edges.filter(e => e.from === selectedNode.id || e.to === selectedNode.id).map(e => findNode(e.from === selectedNode.id ? e.to : e.from)) : [];
  return <Card className="bg-card border-border/50 overflow-hidden" data-id="element-145">
      <CardHeader className="pb-3 pt-4 px-5 border-b border-border/50 bg-muted/10" data-id="element-146">
        <div className="flex items-center justify-between" data-id="element-147">
          <CardTitle className="font-mono text-sm uppercase tracking-wider text-foreground flex items-center gap-2" data-id="element-148">
            <Network className="size-4 text-primary" data-id="element-149" />
            Actor Relationship Graph
          </CardTitle>
          <Badge variant="outline" className="font-mono text-[9px] h-5 border-primary/30 bg-primary/5 text-primary" data-id="element-150">
            INTERACTIVE
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-0" data-id="element-151">
        <div className="grid grid-cols-12" data-id="element-152">
          <div className="col-span-12 lg:col-span-8 relative bg-muted/10 border-r border-border/50" data-id="element-153">
            <svg viewBox="0 0 800 500" className="w-full h-[500px]" data-id="element-154">
              <defs data-id="element-155">
                <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="4" markerHeight="4" orient="auto" data-id="element-156">
                  <path d="M 0 0 L 10 5 L 0 10 z" className="fill-border" data-id="element-157" />
                </marker>
              </defs>

              {/* Edges */}
              {edges.map((e, i) => {
              const a = findNode(e.from);
              const b = findNode(e.to);
              const isActive = selectedNode && (selectedNode.id === e.from || selectedNode.id === e.to);
              return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} className={`transition-all ${isActive ? 'stroke-primary opacity-70' : 'stroke-border/60'}`} strokeWidth={e.strength} data-id="element-158" />;
            })}

              {/* Nodes */}
              {nodes.map(n => <g key={n.id} data-id="element-159">
                  <NodeShape node={n} hovered={hoveredNode === n.id} selected={selectedNode?.id === n.id} onEnter={() => setHoveredNode(n.id)} onLeave={() => setHoveredNode(null)} onClick={() => setSelectedNode(n)} data-id="element-160" />
                  <text x={n.x} y={n.y + 42} textAnchor="middle" className="fill-foreground font-mono text-[9px] pointer-events-none select-none" data-id="element-161">
                    {n.label}
                  </text>
                </g>)}

              {/* Hover tooltip */}
              {hoveredNode && (() => {
              const n = findNode(hoveredNode);
              return <g data-id="element-162">
                      <rect x={n.x + 28} y={n.y - 30} width={170} height={42} rx={4} className="fill-background stroke-border" data-id="element-163" />
                      <text x={n.x + 36} y={n.y - 14} className="fill-foreground text-[10px] font-bold" data-id="element-164">
                        {n.label}
                      </text>
                      <text x={n.x + 36} y={n.y - 2} className="fill-muted-foreground text-[9px] font-mono" data-id="element-165">
                        {typeStyles[n.type].label} · INF {n.influence}
                      </text>
                    </g>;
            })()}
            </svg>

            {/* Legend */}
            <div className="absolute bottom-3 left-3 bg-background/95 border border-border/60 rounded-md p-2 backdrop-blur" data-id="element-166">
              <p className="font-mono text-[9px] uppercase text-muted-foreground mb-1.5" data-id="element-167">
                Legend
              </p>
              <div className="grid grid-cols-1 gap-1 text-[10px]" data-id="element-168">
                {(Object.keys(typeStyles) as NodeType[]).map(t => <div key={t} className="flex items-center gap-1.5" data-id="element-169">
                    <span className={`size-2 rounded-full ${typeStyles[t].fill.replace('fill-', 'bg-')}`} data-id="element-170" />
                    <span className="text-foreground" data-id="element-171">
                      {typeStyles[t].label}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Side detail panel */}
          <div className="col-span-12 lg:col-span-4 p-5 bg-background min-h-[500px] flex flex-col" data-id="element-172">
            {selectedNode ? <>
                <div className="flex items-start justify-between mb-3" data-id="element-173">
                  <div data-id="element-174">
                    <Badge variant="outline" className="font-mono text-[9px] mb-2" data-id="element-175">
                      {typeStyles[selectedNode.type].label}
                    </Badge>
                    <h3 className="font-bold text-base text-foreground" data-id="element-176">
                      {selectedNode.label}
                    </h3>
                  </div>
                  <button onClick={() => setSelectedNode(null)} className="text-muted-foreground hover:text-foreground" data-id="element-177">
                    <X className="size-4" data-id="element-178" />
                  </button>
                </div>

                <div className="bg-muted/30 border border-border/50 rounded-md p-3 mb-4" data-id="element-179">
                  <p className="font-mono text-[10px] text-muted-foreground mb-1 uppercase" data-id="element-180">
                    Influence Score
                  </p>
                  <div className="flex items-center gap-2" data-id="element-181">
                    <span className="text-2xl font-bold font-mono text-primary" data-id="element-182">
                      {selectedNode.influence}
                    </span>
                    <TrendingUp className="size-4 text-emerald-500" data-id="element-183" />
                  </div>
                </div>

                <p className="font-mono text-[10px] text-muted-foreground mb-2 uppercase" data-id="element-184">
                  Metadata
                </p>
                <p className="text-xs text-foreground mb-4 leading-relaxed" data-id="element-185">
                  {selectedNode.meta}
                </p>

                <p className="font-mono text-[10px] text-muted-foreground mb-2 uppercase" data-id="element-186">
                  Koneksi ({connections.length})
                </p>
                <div className="flex-1 space-y-1.5 overflow-y-auto" data-id="element-187">
                  {connections.map(c => <button key={c.id} onClick={() => setSelectedNode(c)} className="w-full text-left p-2 rounded border border-border/50 bg-muted/10 hover:bg-muted/30 hover:border-primary/40 transition flex items-center justify-between" data-id="element-188">
                      <div data-id="element-189">
                        <p className="text-xs font-medium text-foreground" data-id="element-190">
                          {c.label}
                        </p>
                        <p className="font-mono text-[9px] text-muted-foreground" data-id="element-191">
                          {typeStyles[c.type].label}
                        </p>
                      </div>
                      <span className="font-mono text-[10px] text-primary" data-id="element-192">
                        {c.influence}
                      </span>
                    </button>)}
                </div>

                <Button size="sm" className="mt-4 w-full" onClick={() => setSelectedNode(null)} data-id="element-193">
                  Tutup Detail
                </Button>
              </> : <div className="flex-1 flex flex-col items-center justify-center text-center" data-id="element-194">
                <Network className="size-10 text-muted-foreground/30 mb-3" data-id="element-195" />
                <p className="text-sm font-bold text-foreground mb-1" data-id="element-196">
                  Pilih Node
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed max-w-[220px]" data-id="element-197">
                  Klik salah satu node pada graph untuk melihat detail aktor,
                  koneksi, dan skor pengaruh.
                </p>
              </div>}
          </div>
        </div>
      </CardContent>
    </Card>;
}